import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { Modal, Pagination, Tooltip } from '@mui/material';
import CreateDatabaseModal from './Modal/CreateDatabase';
import { useHistory, useLocation } from 'react-router-dom';
import { GlobalContext } from 'providers/GlobalProvider';
import PriceCheckOutlinedIcon from '@mui/icons-material/PriceCheckOutlined';
import MasterTable from './Tables/MasterTable';
import RollUpStats from './Cards/RollUpStats';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import CardDB from './Cards/CardDB';
import CircularProgress from '@mui/material/CircularProgress';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import DeleteCommonModal from 'components/Monitor/Modals/DeleteModal';
import ChangeStatus from './Modal/ChangeStatus';
import ConfirmStatusChangeModal from './Modal/ConfirmStatusChangeModal';
import { enqueueSnackbar } from 'notistack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import { PaginationParams } from '../../api/bitedb/tariffs/tariffs';

import { useQueryClient } from '@tanstack/react-query';
import {
  STATS_QUERY_KEYS,
  useGetStatsByGroupId,
} from 'api/bitedb/stats/stats.hooks';
import TariffCols from './Tables/TariffCols';
import TariffRow from './Tables/TariffRow';
import PaginationJump from './common/PaginationJump';
import {
  useDeleteTariff,
  useGetTariffsByGroupId,
  useUpdateTariffStatus,
} from 'api/bitedb/tariffs/tariffs.hooks';

// Helper Hook for Debouncing
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

interface DashboardProps {
  databases: any[];
  isLoading: boolean;
  searchTerm: string;
  setSearchTerm: any;
  tariffCount: number;
  processingDatabases?: Set<string>;
  onDatabaseCreated?: (databaseId: string) => void;
  onDatabaseUploadFailed?: (databaseId: string) => void;
  onOfferLinkEntities?: (databaseId: string) => void;
  headerActions?: React.ReactNode; // <--- ADDED PROP
}

const Tariffs: React.FC<DashboardProps> = ({
  databases,
  isLoading,
  searchTerm,
  setSearchTerm,
  processingDatabases,
  onDatabaseCreated,
  onDatabaseUploadFailed,
  onOfferLinkEntities,
  headerActions, // <--- DESTRUCTURED PROP
}) => {
  const { cur_user } = useContext(GlobalContext);
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();

  // URL state management
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const page = parseInt(queryParams.get('page') || '1', 10);
  const limit = parseInt(queryParams.get('limit') || '10', 10);
  const sortBy = queryParams.get('sortBy') || 'hit_count';
  const sortOrder = (queryParams.get('sortOrder') as 'asc' | 'desc') || 'desc';
  const urlSearchTerm = queryParams.get('search') || '';

  // Local state
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [showCreateDatabaseModal, setShowCreateDatabaseModal] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState(urlSearchTerm);
  const [selectedDB] = useState<string>('');
  const [isDeletingItems, setIsDeletingItems] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [currentItems, setCurrentItems] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState<
    'review_queue' | 'all_tariffs' | 'new' | 'quarentined'
  >('review_queue');
  const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [showTariffGuide, setShowTariffGuide] = useState(false);

  useEffect(() => {
    if (Array.isArray(databases) && databases.length) {
      const filteredDatabases = databases.filter((db) => db.name !== 'All');
      databases.splice(
        0,
        databases.length,
        { name: 'All' },
        ...filteredDatabases,
      );
    }
  }, [databases]);

  // Debounced search term
  const debouncedSearchTerm = useDebounce(localSearchTerm, 500);

  // Database pagination
  const itemsPerPage = 9;

  const handleUrlChange = useCallback(
    (newParams: Record<string, string | number>) => {
      const updatedParams = new URLSearchParams(location.search);
      Object.entries(newParams).forEach(([key, value]) => {
        if (value) {
          updatedParams.set(key, String(value));
        } else {
          updatedParams.delete(key);
        }
      });
      history.replace({ search: updatedParams.toString() });
    },
    [history, location.search],
  );

  useEffect(() => {
    if (debouncedSearchTerm !== urlSearchTerm) {
      handleUrlChange({ search: debouncedSearchTerm, page: 1 });
    }
  }, [debouncedSearchTerm, urlSearchTerm, handleUrlChange]);

  const paginationParams: PaginationParams = {
    page: page,
    limit: limit,
    sortBy: sortBy,
    sortOrder: sortOrder,
    exclusions:
      viewMode === 'review_queue' ? ['cleared', 'quarantined', 'barred'] : [],
    ...(selectedDB && selectedDB !== '' && { databaseName: selectedDB }),
  };

  const {
    data: tariffsData,
    pagination: tariffsPagination,
    isLoading: isTariffsLoading,
    error: tariffsError,
    refetch: refetchTariffs,
  } = useGetTariffsByGroupId(
    cur_user?.selected_team?.id ?? '-1',
    debouncedSearchTerm,
    paginationParams,
  );

  // Mutation hooks
  const deleteTariffMutation = useDeleteTariff();
  const updateTariffStatusMutation = useUpdateTariffStatus();
  const isStatusUpdating = updateTariffStatusMutation.isPending;

  useEffect(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    if (databases.length > 0) {
      setCurrentItems(
        databases
          .filter((item: { type: string }) => item.type === 'tariffs')
          .slice(indexOfFirstItem, indexOfLastItem),
      );
      setTotalPages(
        Math.ceil(
          databases.filter((item: { type: string }) => item.type === 'tariffs')
            .length / itemsPerPage,
        ),
      );
    } else {
      setTotalPages(1);
      setCurrentPage(1);
    }
  }, [currentPage, itemsPerPage, databases]);

  // Handle tariffs loading errors
  useEffect(() => {
    if (tariffsError) {
      enqueueSnackbar('Failed to fetch tariffs', { variant: 'error' });
    }
  }, [tariffsError]);

  const handleSelectAll = () => {
    setSelectedItems(
      selectedItems.length === tariffsData?.length ? [] : tariffsData || [],
    );
  };

  const handleSort = (key: string) => {
    const newSortOrder = sortBy === key && sortOrder === 'asc' ? 'desc' : 'asc';
    handleUrlChange({ sortBy: key, sortOrder: newSortOrder });
  };

  const handlePageChange = (e: ChangeEvent<unknown>, new_page: number) => {
    setCurrentPage(new_page);
  };

  // Handle tariff table pagination
  const handleTariffPageChange = (
    event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    handleUrlChange({ page: newPage });
  };

  const getParagraphText = (database: any) => {
    let paragraphText = 'Never updated';

    if (database.last_screen_timestamp) {
      const lastScreenedDate = new Date(database.last_screen_timestamp);
      const today = new Date();
      const diffTime = Math.abs(today.getTime() - lastScreenedDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      paragraphText = `Updated ${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }
    return paragraphText;
  };

  const handleBulkAction = async (
    action: 'delete' | 'updateStatus',
    status?: string,
  ) => {
    const ids = selectedItems.map((item) => item.tariff_id);
    const updateIds = selectedItems.map((item) => item.association_id);

    if (ids.length === 0) {
      return enqueueSnackbar('Please select at least one tariff', {
        variant: 'error',
      });
    }

    const mutationFn =
      action === 'delete'
        ? deleteTariffMutation.mutateAsync
        : updateTariffStatusMutation.mutateAsync;
    const actionPastTense = action === 'delete' ? 'deleted' : 'updated';

    const targetIds = action === 'delete' ? ids : updateIds;

    const promises = targetIds.map((id) =>
      action === 'delete'
        ? (mutationFn as any)(id)
        : (mutationFn as any)({ id: id, statusData: { status_flag: status } }),
    );

    try {
      await Promise.all(promises);

      // Invalidate tariffs cache for this group
      queryClient.invalidateQueries({
        queryKey: ['tariffs', cur_user?.selected_team?.id],
        exact: false,
      });

      // Clear stats
      queryClient.invalidateQueries({
        queryKey: [STATS_QUERY_KEYS.stats, cur_user?.selected_team?.id],
        exact: false,
      });

      enqueueSnackbar(`Tariffs ${actionPastTense} successfully`, {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(`Some tariffs failed to be ${actionPastTense}`, {
        variant: 'error',
      });
    } finally {
      setSelectedItems([]);
      setIsDeletingItems(false);
      setIsChangingStatus(false);
      setShowStatusConfirmModal(false);
      setPendingStatus(null);
    }
  };

  const handleDeleteTariff = async () => {
    await handleBulkAction('delete');
  };

  const handleEdit = async (item: any) => {
    setSelectedItems((prevSelected: any[]) => {
      const safePrevSelected = Array.isArray(prevSelected) ? prevSelected : [];
      const alreadySelected = safePrevSelected.some((i) => i.id === item.id);
      if (alreadySelected) {
        return safePrevSelected;
      }
      return [...safePrevSelected, item];
    });
    setIsChangingStatus(true);
  };

  const handleDelete = async (item: any) => {
    setSelectedItems((prevSelected: any[]) => {
      const safePrevSelected = Array.isArray(prevSelected) ? prevSelected : [];
      const alreadySelected = safePrevSelected.some((i) => i.id === item.id);
      return alreadySelected
        ? safePrevSelected.filter((i) => i.id !== item.id)
        : [...safePrevSelected, item];
    });
    setIsDeletingItems(true);
  };

  const handleStatusSelection = (status: string) => {
    if (!status) {
      enqueueSnackbar('Please select a status', { variant: 'warning' });
      return;
    }
    if (selectedItems.length === 0) {
      enqueueSnackbar('Please select at least one tariff', {
        variant: 'error',
      });
      return;
    }
    setPendingStatus(status);
    setShowStatusConfirmModal(true);
    setIsChangingStatus(false);
  };

  const closeStatusConfirmation = () => {
    setShowStatusConfirmModal(false);
    setPendingStatus(null);
  };

  const confirmStatusChange = () => {
    if (!pendingStatus) {
      return;
    }
    handleBulkAction('updateStatus', pendingStatus);
  };

  const getCreatedText = (database: any) => {
    if (database.created_at) {
      const createdDate = new Date(database.created_at);
      return `Created on ${createdDate.toLocaleDateString()}`;
    }
    return 'Creation date unknown';
  };

  const { data: stats } = useGetStatsByGroupId(
    cur_user?.selected_team?.id ?? '-1',
  );

  const handleScrollToDatabases = () => {
    const element = document.getElementById('your-databases-section');
    if (element) {
      const elementPosition = element.offsetTop + 55; // Adjust offset if needed, matching Dashboard.tsx
      const offsetPosition = elementPosition;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div
      className='team-content-container margined-container'
      style={{ paddingTop: '0px' }}
    >
      <div className='people-col'>
        <div className='team-content-header'>
          <div className='team-content-search people-col'>
            <div className='card-db-header'>
              <div className='card-db-icon'>
                <PriceCheckOutlinedIcon
                  sx={{ fontSize: 20, color: 'var(--Primary-400, #4895ef)' }}
                />
              </div>
              <div className='card-db-title'>Tariff Analyzer</div>
            </div>
            <span className='card-db-meta-span'>
              Upload, calculate and store duty and tariffs data related to your
              products.
            </span>
          </div>

          <div className='module-header-title-conts'>
            <div
              className='module-header-btns'
              style={{ display: 'flex', alignItems: 'center', gap: '16px' }}
            >
              {headerActions}
              <div
                className='create-new-team-btn'
                onClick={handleScrollToDatabases}
                style={{
                  width: 'fit-content',
                  height: '36px',
                  margin: 0,
                  padding: '0 16px',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div
                  className='create-new-team-btn-text'
                  style={{ color: 'var(--text-primary)', margin: 0 }}
                >
                  My Databases
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className='api-container-info'
        style={{ padding: '0px 0px 0px 0px', width: '100%' }}
      >
        <div
          className='card-grid-six'
          style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}
        >
          <RollUpStats
            svgIcon={
              <PriceCheckOutlinedIcon
                sx={{ fontSize: 20, color: 'var(--Primary-400, #4895ef)' }}
              />
            }
            title={'All HTS Codes'}
            total={`${stats?.total_tariffs ?? '0'}`}
            goTo={() => {
              setViewMode('all_tariffs');
              handleUrlChange({ page: 1 });
            }}
          />
          <RollUpStats
            svgIcon={
              <ReportProblemOutlinedIcon
                sx={{ fontSize: 20, color: 'var(--Primary-400, #4895ef)' }}
              />
            }
            title={'Needs Review'}
            // total={'0'}
            total={`${stats?.total_not_reviewed_tariffs || 0} `}
            // total={`${stats?.total_cleared_tariffs ?? '0'}`}
            goTo={() => {
              setViewMode('review_queue');
              handleUrlChange({ page: 1 });
            }}
          />
          <RollUpStats
            svgIcon={
              <CalendarTodayOutlinedIcon
                sx={{ fontSize: 20, color: 'var(--Primary-400, #16a34a)' }}
              />
            }
            title={'Recently Updated'}
            total={`${stats?.total_new_tariffs ?? '0'}`}
            goTo={() => {
              setViewMode('new');
              handleUrlChange({ page: 1 });
            }}
          />
          <RollUpStats
            svgIcon={
              <WorkOutlineOutlinedIcon
                sx={{ fontSize: 20, color: 'var(--Primary-400, #f59e0b)' }}
              />
            }
            title={'Archived Records'}
            total={`${stats?.total_quarantined_tariffs ?? 0}`}
            goTo={() => {
              setViewMode('quarentined');
              handleUrlChange({ page: 1 });
            }}
          />
        </div>
      </div>
      <div
        style={{
          width: '100%',
          marginTop: '20px',
          marginBottom: '20px',
        }}
      >
        <button
          type='button'
          onClick={() => setShowTariffGuide((prev) => !prev)}
          style={{
            width: '100%',
            background: 'var(--Background-200, var(--bg-colors))',
            border: '1px solid var(--base-border)',
            borderRadius: '8px',
            minHeight: '44px',
            color: 'var(--text-primary)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 14px',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '24px',
            }}
          >
            <HelpOutlineOutlinedIcon
              sx={{ fontSize: 18, color: 'var(--Primary-400, #4895ef)' }}
            />
            <span className='card-db-title'>
              New to the Tariff Analyzer? Learn how statuses work and tips for
              clean uploads
            </span>
          </span>
          {showTariffGuide ? (
            <ExpandLessOutlinedIcon
              sx={{ fontSize: 20, color: 'var(--card-text)' }}
            />
          ) : (
            <ExpandMoreOutlinedIcon
              sx={{ fontSize: 20, color: 'var(--card-text)' }}
            />
          )}
        </button>

        {showTariffGuide && (
          <div
            style={{
              display: 'flex',
              gap: '16px',
              marginTop: '16px',
              flexWrap: 'wrap',
            }}
          >
            <div
              style={{
                flex: '1 1 420px',
                background: 'var(--Background-200, var(--bg-colors))',
                border: '1px solid var(--base-border)',
                borderRadius: '10px',
                padding: '18px',
              }}
            >
              <div className='api-container-left-h1'>Record Status Guide</div>
              <div
                className='card-db-meta-span'
                style={{ marginTop: '6px', display: 'block' }}
              >
                Each record is assigned a status based on data completeness and
                tariff compliance checks.
              </div>

              <div
                style={{
                  marginTop: '16px',
                  border: '1px solid #1B7852',
                  borderRadius: '8px',
                  background: 'rgba(22, 163, 74, 0.08)',
                  padding: '12px',
                }}
              >
                <div className='card-db-title'>
                  <CheckCircleOutlineOutlinedIcon
                    sx={{ color: '#22C55E', fontSize: 18, marginRight: '6px' }}
                  />
                  Green — Complete
                </div>
                <div
                  className='card-db-meta-span'
                  style={{ marginTop: '6px', lineHeight: '1.5' }}
                >
                  All required checks pass: valid 10-digit HTS code, valid
                  2-letter country of origin, and loading date is not after
                  entry date. No critical blockers or warning flags were
                  detected.
                </div>
              </div>

              <div
                style={{
                  marginTop: '12px',
                  border: '1px solid #C68A18',
                  borderRadius: '8px',
                  background: 'rgba(245, 158, 11, 0.08)',
                  padding: '12px',
                }}
              >
                <div className='card-db-title'>
                  <WarningAmberOutlinedIcon
                    sx={{ color: '#F59E0B', fontSize: 18, marginRight: '6px' }}
                  />
                  Yellow — Review Needed
                </div>
                <ul
                  className='card-db-meta-span'
                  style={{ margin: '6px 0 0 20px', lineHeight: '1.5' }}
                >
                  <li>
                    FTA option is available, but Use FTA was not selected.
                  </li>
                  <li>
                    Common exemption codes are available for this HTS/origin
                    combination.
                  </li>
                  <li>
                    Smelt/pour country was provided, but material percentage is
                    still defaulted.
                  </li>
                  <li>Material percentage is using the default value (40%).</li>
                </ul>
              </div>

              <div
                style={{
                  marginTop: '12px',
                  border: '1px solid #AD2747',
                  borderRadius: '8px',
                  background: 'rgba(239, 68, 68, 0.08)',
                  padding: '12px',
                }}
              >
                <div className='card-db-title'>
                  <HighlightOffOutlinedIcon
                    sx={{ color: '#EF4444', fontSize: 18, marginRight: '6px' }}
                  />
                  Red — Critical / Barred
                </div>
                <ul
                  className='card-db-meta-span'
                  style={{ margin: '6px 0 0 20px', lineHeight: '1.5' }}
                >
                  <li>
                    Country of origin is missing or not a 2-letter ISO code.
                  </li>
                  <li>
                    HTS code is invalid (not 10 digits) or not found in the HTS
                    document.
                  </li>
                  <li>Loading date is after entry date.</li>
                  <li>
                    High-duty aluminum code detected (9903.85.67 / 9903.85.68)
                    and smelt/pour country is missing.
                  </li>
                  <li>
                    A specific-per-unit duty applies, but unit quantity/weight
                    input is missing.
                  </li>
                </ul>
              </div>
            </div>

            <div
              style={{
                flex: '1 1 420px',
                background: 'var(--Background-200, var(--bg-colors))',
                border: '1px solid var(--base-border)',
                borderRadius: '10px',
                padding: '18px',
              }}
            >
              <div
                className='api-container-left-h1'
                style={{
                  paddingTop: '0px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <TipsAndUpdatesOutlinedIcon
                  sx={{ fontSize: 18, color: 'var(--Primary-400, #4895ef)' }}
                />
                Upload Tips
              </div>
              <div
                className='card-db-meta-span'
                style={{ marginTop: '6px', lineHeight: '1.5' }}
              >
                Follow these guidelines to reduce errors and avoid unnecessary
                red or yellow flags when uploading bulk tariff data.
              </div>

              <ol
                className='card-db-meta-span'
                style={{ margin: '12px 0 0 20px', lineHeight: '1.6' }}
              >
                <li>
                  Use full HTS codes — include all digits (for example:{' '}
                  <code>7304.29.41.30</code>).
                </li>
                <li>
                  Use ISO country codes — two-letter ISO 3166-1 alpha-2 country
                  values (for example:
                  <code> CN</code>, <code> MX</code>).
                </li>
                <li>
                  Include product value — each row should include a numeric
                  product value.
                </li>
                <li>
                  Verify origin details for high-duty aluminum — smelt/pour
                  country is required when 9903.85.67 or 9903.85.68 applies.
                </li>
                <li>
                  Keep one product per row — do not combine products in a single
                  line.
                </li>
                <li>
                  Use the CSV template from the toolbar so column names are
                  exact.
                </li>
                <li>
                  Keep HTS codes current — outdated or invalid codes will be
                  marked as critical.
                </li>
              </ol>

              <div
                style={{
                  marginTop: '18px',
                  borderTop: '1px solid var(--base-border)',
                  paddingTop: '12px',
                }}
              >
                <div className='card-db-title'>Default Values</div>
                <div
                  className='card-db-meta-span'
                  style={{ lineHeight: '1.6' }}
                >
                  These values are used if they are omitted in the upload:
                  <br />
                  Product Value: $1,000
                  <br />
                  Transportation Method: Ocean
                  <br />
                  Entry Date and Loading Date: Current date (upload date)
                  <br />
                  Country of Pour / Smelt / Loading: Origin country value
                  <br />
                  Material Split Percentage: 40% of product value for line 1
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <div
          className='api-container-left-h1'
          id='your-review-queue-section'
          style={{ paddingTop: '0px' }}
        >
          {viewMode === 'review_queue'
            ? 'Your Review Queue'
            : viewMode === 'new'
            ? 'Recently Updated'
            : viewMode === 'quarentined'
            ? 'Archived Records'
            : 'All HTS Codes'}
        </div>
      </div>
      <div className='team-content-header'>
        <div className='team-content-search'>
          <div className='screening-input-field-container'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='20'
              height='20'
              viewBox='0 0 20 20'
              fill='none'
            >
              <path
                d='M17.5 17.5L14.5834 14.5833M16.6667 9.58333C16.6667 13.4954 13.4954 16.6667 9.58333 16.6667C5.67132 16.6667 2.5 13.4954 2.5 9.58333C2.5 5.67132 5.67132 2.5 9.58333 2.5C13.4954 2.5 16.6667 5.67132 16.6667 9.58333Z'
                stroke='var(--card-text)'
                strokeWidth='1.66667'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
            <input
              placeholder='Search Transactions'
              id='primary'
              type='text'
              className='screening-input-field-content'
              name='primary'
              value={localSearchTerm}
              onChange={(e) => {
                setLocalSearchTerm(e.target.value);
              }}
            />
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            width: '40%',
            gap: '10px',
            justifyContent: 'flex-end',
          }}
        >
          <Tooltip
            title={'Update Status'}
            placement='bottom-start'
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  display: 'flex',
                  padding: 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  alignSelf: 'stretch',
                  borderRadius: 'var(--radius-md, 8px)',
                  background: 'var(--Background-200, var(--bg-colors))',
                  color: 'var(--text-prim25)',
                  maxWidth: '700px',
                  '& .MuiTooltip-arrow': {
                    color: 'var(--Background-200, var(--bg-colors))',
                  },
                },
              },
            }}
          >
            <div
              className='audit-btn-star'
              onClick={() => setIsChangingStatus(true)}
            >
              <EditOutlinedIcon
                sx={{ fontSize: 20, color: 'var(--card-text)' }}
              />
            </div>
          </Tooltip>
          <Tooltip
            title={'Delete Tariffs'}
            placement='bottom-start'
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  display: 'flex',
                  padding: 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  alignSelf: 'stretch',
                  borderRadius: 'var(--radius-md, 8px)',
                  background: 'var(--Background-200, var(--bg-colors))',
                  color: 'var(--text-prim25)',
                  maxWidth: '700px',
                  '& .MuiTooltip-arrow': {
                    color: 'var(--Background-200, var(--bg-colors))',
                  },
                },
              },
            }}
          >
            <div
              className='audit-btn-star'
              onClick={() => setIsDeletingItems(true)}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='20'
                height='20'
                viewBox='0 0 20 20'
                fill='none'
              >
                <path
                  d='M7.5 2.5H12.5M2.5 5H17.5M15.8333 5L15.2489 13.7661C15.1612 15.0813 15.1174 15.7389 14.8333 16.2375C14.5833 16.6765 14.206 17.0294 13.7514 17.2497C13.235 17.5 12.5759 17.5 11.2578 17.5H8.74221C7.42409 17.5 6.76503 17.5 6.24861 17.2497C5.79396 17.0294 5.41674 16.6765 5.16665 16.2375C4.88259 15.7389 4.83875 15.0813 4.75107 13.7661L4.16667 5M8.33333 8.75V12.9167M11.6667 8.75V12.9167'
                  stroke='var(--card-text)'
                  strokeWidth='1.66667'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
          </Tooltip>
        </div>
      </div>

      <MasterTable
        cols={
          <TariffCols
            showFilters
            handleCheckboxClick={handleSelectAll}
            isSelectedItems={
              selectedItems.length === tariffsData?.length &&
              tariffsData?.length > 0
            }
            sortData={handleSort}
            sortConfig={{ key: sortBy, direction: sortOrder }}
          />
        }
        loading={isTariffsLoading}
        suppliersData={tariffsData}
        location={'/tariff'}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        currentPage={page}
        totalPages={tariffsPagination?.totalPages || 1}
        onPageChange={handleTariffPageChange}
        customRowComponent={(props: any) => (
          <TariffRow
            location={`/tariff/${props.transaction.id}`}
            viewType='Tariff View'
            transaction={props.transaction}
            selectedItems={props.selectedItems}
            setSelectedItems={props.setSelectedItems}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
          />
        )}
      />

      <div
        className='api-container'
        style={{ gap: '10px', padding: '0px 0px 0px 0px', width: '100%' }}
      >
        <div className='api-container-left' style={{ flex: '1', gap: '20px' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <div
              className='api-container-left-h1'
              id='your-databases-section'
              style={{ paddingTop: '0px' }}
            >
              Your Databases {`(${stats?.total_tariff_databases ?? 0})`}
            </div>
          </div>

          <div className='team-content-header'>
            <div className='team-content-search'>
              <div className='screening-input-field-container'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                >
                  <path
                    d='M17.5 17.5L14.5834 14.5833M16.6667 9.58333C16.6667 13.4954 13.4954 16.6667 9.58333 16.6667C5.67132 16.6667 2.5 13.4954 2.5 9.58333C2.5 5.67132 5.67132 2.5 9.58333 2.5C13.4954 2.5 16.6667 5.67132 16.6667 9.58333Z'
                    stroke='var(--card-text)'
                    strokeWidth='1.66667'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
                <input
                  placeholder='Search Databases'
                  id='primary'
                  type='text'
                  className='screening-input-field-content'
                  name='primary'
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                />
              </div>
            </div>
          </div>
          <div
            className='audit-dashboard-cards-frame-box'
            style={{ width: '100%', height: '470px' }}
          >
            {isLoading ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <CircularProgress color='primary' />
              </div>
            ) : databases.length === 0 ? (
              <div className='audit-dashboard-cards-no-ticket'>
                No Databases Found
              </div>
            ) : (
              <div
                className='audit-dashboard-cards-frame'
                style={{ width: '100%' }}
              >
                {currentItems.map((database: any, index: number) => {
                  return (
                    <CardDB
                      key={index}
                      id={database.id}
                      svgIcon={
                        database.type === 'products' ? (
                          <Inventory2OutlinedIcon
                            sx={{
                              fontSize: 20,
                              color: 'var(--Primary-400, #4895ef)',
                            }}
                          />
                        ) : (
                          <PriceCheckOutlinedIcon
                            sx={{
                              fontSize: 20,
                              color: 'var(--Primary-400, #4895ef)',
                            }}
                          />
                        )
                      }
                      title={database.name}
                      createdAt={getCreatedText(database)}
                      paragraph={getParagraphText(database)}
                      total={(
                        (parseInt(database.nr_people_and_companies, 10) || 0) +
                        (parseInt(database.nr_products, 10) || 0) +
                        (parseInt(database.nr_tariffs, 10) || 0)
                      ).toString()}
                      category={database.type}
                      isProcessing={processingDatabases?.has(
                        database.id.toString(),
                      )}
                    />
                  );
                })}
              </div>
            )}
          </div>
          {databases.length > 9 && (
            <div
              className='hb-pagination'
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                gap: '16px',
                flexWrap: 'wrap',
              }}
            >
              <Pagination
                onChange={handlePageChange}
                size='large'
                count={totalPages}
                shape='rounded'
                variant='text'
                page={currentPage}
              />
              <PaginationJump
                totalPages={totalPages}
                currentPage={currentPage}
                onJump={(pageNumber) =>
                  handlePageChange({} as ChangeEvent<unknown>, pageNumber)
                }
              />
            </div>
          )}
        </div>
      </div>

      <Modal
        open={showCreateDatabaseModal}
        onClose={() => setShowCreateDatabaseModal(false)}
      >
        <div style={{ outline: 'none' }}>
          <CreateDatabaseModal
            open={showCreateDatabaseModal}
            handleClose={() => setShowCreateDatabaseModal(false)}
            handleRefresh={refetchTariffs}
            onDatabaseCreated={onDatabaseCreated}
            onDatabaseUploadFailed={onDatabaseUploadFailed}
            onOfferLinkEntities={onOfferLinkEntities}
          />
        </div>
      </Modal>
      <Modal
        open={isDeletingItems}
        onClose={() => {
          setIsDeletingItems(false);
          setSelectedItems([]);
        }}
      >
        <DeleteCommonModal
          handleClose={() => {
            setIsDeletingItems(false);
            setSelectedItems([]);
          }}
          handleDelete={handleDeleteTariff}
          title={`Delete ${selectedItems.length} item from the Review?`}
          paragraph={
            'Once deleted there is no way to recover these items in both the database and or in either product tab view or the tariff tab view.'
          }
          isLoading={false}
        />
      </Modal>
      <Modal
        open={isChangingStatus && !showStatusConfirmModal}
        onClose={() => {
          setIsChangingStatus(false);
        }}
      >
        <ChangeStatus
          isChangingStatus={isStatusUpdating}
          handleClose={() => {
            setIsChangingStatus(false);
          }}
          curStatus={'clear'}
          changeStatus={handleStatusSelection}
        />
      </Modal>
      <Modal open={showStatusConfirmModal} onClose={closeStatusConfirmation}>
        <ConfirmStatusChangeModal
          count={selectedItems.length}
          status={pendingStatus}
          onCancel={closeStatusConfirmation}
          onConfirm={confirmStatusChange}
          isProcessing={isStatusUpdating}
        />
      </Modal>
    </div>
  );
};

export default Tariffs;
